import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useIsAuthenticated } from '@azure/msal-react';
import { scanService } from '../services/api';
import { validateService } from '../services/validateService';
import tierService from '../services/tierService';
import { createAsset } from '../services/assetService';
import ValidateResults from './ValidateResults';
import HttpTransactionView, { parseHttpTransactionFromRaw } from './common/HttpTransactionView';
import './ScanResults.css';

/**
 * ScanResults - Fast Scan Results Page (v3.0)
 * Phase 7: Migrated from inline styles to CSS classes.
 * All colors now come from CSS variables — no JS color constants needed.
 */

const ScanResults = () => {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    targetProfile: true,
    attackSurface: true,
    attackSurfaceDetails: false,
    discoveredUrls: false,
    r3dteamBrief: true,
    vulnerabilities: false,
    validateResults: true,
    defensivePosture: true,
    recommendations: true,
    toolSummaries: false,
    rawToolData: false,
    fullJson: false
  });
  const [showAllVulns, setShowAllVulns] = useState(false);

  // VALIDATE feature state
  const [validateJobId, setValidateJobId] = useState(null);
  const [validateStatus, setValidateStatus] = useState(null);

  // Tier and conversion state
  const [tierInfo, setTierInfo] = useState(null);
  const [showConversionModal, setShowConversionModal] = useState(false);
  const [assetAdded, setAssetAdded] = useState(false);
  const [addingAsset, setAddingAsset] = useState(false);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [expandedVulns, setExpandedVulns] = useState({});

  const { scanId } = useParams();
  const navigate = useNavigate();
  const isAuthenticated = useIsAuthenticated();

  useEffect(() => {
    if (!scanId) {
      setError('No scan ID provided');
      setLoading(false);
      return;
    }
    fetchResults(scanId);
  }, [scanId]);

  // Load tier info
  useEffect(() => {
    const loadTier = async () => {
      try {
        const info = await tierService.getCachedTierInfo();
        setTierInfo(info);
      } catch (err) {
        // Non-critical — treat as free/unauthenticated
      }
    };
    loadTier();
  }, []);

  // Conversion modal for unauthenticated users — 10s after results load
  useEffect(() => {
    if (!loading && results && !isAuthenticated) {
      const dismissed = localStorage.getItem('driftalarm_signupDismissed');
      if (dismissed === 'true') return;
      const timer = setTimeout(() => setShowConversionModal(true), 10000);
      return () => clearTimeout(timer);
    }
  }, [loading, results, isAuthenticated]);

  const fetchResults = async (scanId) => {
    try {
      setLoading(true);
      const data = await scanService.getScanResult(scanId);
      if (!data) {
        setError('Scan results not found.');
        setLoading(false);
        return;
      }
      setResults(data);

      // Check if this scan has a VALIDATE job
      if (data.validateJobId) {
        setValidateJobId(data.validateJobId);
        try {
          const validateStatusData = await validateService.getValidateStatus(data.validateJobId);
          setValidateStatus(validateStatusData.status);
        } catch (valErr) {
          console.log('Could not fetch VALIDATE status:', valErr.message);
        }
      }

      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  const formatDate = (iso) => {
    if (!iso) return 'N/A';
    const d = new Date(iso);
    return isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getSeverityKey = (severity) => {
    if (!severity) return '';
    return severity.toLowerCase();
  };

  const getSeverityOrder = (severity) => {
    const order = { 'critical': 0, 'high': 1, 'medium': 2, 'low': 3, 'info': 4 };
    return order[severity?.toLowerCase()] ?? 5;
  };

  const tier = tierInfo?.tier || 'free';
  const fastScanUsed = tierInfo?.limits?.fastScans?.used || 0;
  const fastScanLimit = tierInfo?.limits?.fastScans?.limit || 5;
  const isPaidTier = ['standard', 'pro', 'enterprise'].includes(tier);

  const handleAddAsset = async () => {
    if (addingAsset || assetAdded || !results?.target) return;
    setAddingAsset(true);
    try {
      await createAsset('domain', results.target);
      setAssetAdded(true);
    } catch (err) {
      console.error('Failed to add asset:', err);
    } finally {
      setAddingAsset(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (pdfGenerating || !results) return;
    setPdfGenerating(true);
    try {
      const { pdf } = await import('@react-pdf/renderer');
      const { default: saveAs } = await import('file-saver');
      const { default: ScanReportPDFComp } = await import('./reports/ScanReportPDF');
      const element = React.createElement(ScanReportPDFComp, {
        scanResult: results,
        domain: results.target,
        scanDate: results.timestamp
      });
      const blob = await pdf(element).toBlob();
      saveAs(blob, `driftalarm-scan-${results.target || 'report'}-${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (err) {
      console.error('PDF generation failed:', err);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setPdfGenerating(false);
    }
  };

  const dismissConversionModal = () => {
    setShowConversionModal(false);
    localStorage.setItem('driftalarm_signupDismissed', 'true');
  };

  // Fast-scan riskScore polarity is higher=healthier (88=Healthy, 43=At Risk), so the
  // headline color must key off the LEVEL string, not the score. (A prior score->severity
  // helper treated a higher score as worse — inverted for this polarity — and
  // getSeverityKey('At Risk') yields the space-broken class 'at risk' with no CSS rule.)
  // severityKeyOf handles BOTH the fast-scan level vocabulary and the AI threat-word one.
  const LEVEL_TO_SEVERITY = { healthy: 'low', caution: 'medium', 'at risk': 'high', critical: 'critical' };
  const severityKeyOf = (s) => {
    const k = (s || '').toLowerCase();
    if (LEVEL_TO_SEVERITY[k]) return LEVEL_TO_SEVERITY[k];
    if (['critical', 'high', 'medium', 'low', 'info'].includes(k)) return k;
    return 'info';
  };
  const SEVERITY_RANK = { critical: 4, high: 3, 'at risk': 3, medium: 2, caution: 2, low: 1, healthy: 1, info: 0 };
  const severityRank = (s) => SEVERITY_RANK[(s || '').toLowerCase()] ?? 0;

  if (loading) {
    return (
      <div className="scan-results-container">
        <div className="sr-loading">
          <div className="sr-spinner"></div>
          <p className="sr-loading-text">Analyzing Target...</p>
        </div>
      </div>
    );
  }

  if (error || !results) {
    return (
      <div className="scan-results-container">
        <div className="sr-error">
          <h2 className="sr-error-title">{error ? 'ERROR' : 'NO RESULTS'}</h2>
          <p className="sr-error-text">{error || 'No scan results found'}</p>
          <button onClick={() => navigate('/fast-scan')} className="sr-error-btn">
            Return to Fast Scan
          </button>
        </div>
      </div>
    );
  }

  // Extract data
  const dns = results.dns || {};
  const geo = results.geolocation || {};
  // RDAP/WHOIS domain registration lives under `results.registration` — `results.whois`
  // was always empty, so the Registrar field rendered 'N/A'.
  const registration = results.registration || results.whois || {};
  const dnsRecordRows = [
    ['A', dns.aRecords], ['AAAA', dns.aaaaRecords], ['CNAME', dns.cnameRecords],
    ['MX', dns.mxRecords], ['NS', dns.nsRecords], ['TXT', dns.txtRecords]
  ].filter(([, v]) => Array.isArray(v) && v.length > 0);
  const naabu = results.naabu || {};
  const zgrab2 = results.zgrab2 || {};
  const httpx = results.httpx || {};
  const katana = results.katana || {};
  const subfinder = results.subfinder || {};
  const nuclei = results.nuclei || {};
  const wafw00f = results.wafw00f || {};
  const ai = results.aiAnalysis || {};
  const metadata = results.metadata || {};
  const execSummary = results.executiveSummary || {};
  const webTech = results.webTechnology || {};

  // Statistics from executiveSummary
  const stats = execSummary.statistics || {};

  // Sort vulnerabilities by severity (highest first)
  const allVulns = nuclei.vulnerabilities || [];
  const sortedVulns = [...allVulns].sort((a, b) => getSeverityOrder(a.severity) - getSeverityOrder(b.severity));
  const displayVulns = showAllVulns ? sortedVulns.slice(0, 15) : sortedVulns.slice(0, 5);

  // Combine technologies from multiple sources
  const allTechnologies = [
    ...(httpx.technologies || []),
    ...(webTech.technologies || [])
  ].filter((v, i, a) => a.indexOf(v) === i);

  // WP4d (defense in depth): show the WORSE of the deterministic level and the AI's
  // overall threat so the headline never under-represents risk (e.g. an exposed-service
  // site the deterministic scorer rated benign but the AI flagged High).
  const aiThreatLevel = ai.overallThreatLevel || '';
  const headlineLevel = severityRank(aiThreatLevel) > severityRank(results.riskLevel)
    ? aiThreatLevel
    : (results.riskLevel || 'Unknown');
  const riskSeverity = severityKeyOf(headlineLevel);

  return (
    <div className="scan-results-container">

      {/* ==================== HEADER ==================== */}
      <div className="sr-header">
        <h1 className="sr-header-title">Fast Scan Results</h1>
        <p className="sr-header-subtitle">
          Security Assessment Complete • AI-Powered Analysis
        </p>
        {isPaidTier && (
          <button
            className="sr-btn-secondary sr-pdf-btn"
            onClick={handleDownloadPDF}
            disabled={pdfGenerating}
          >
            {pdfGenerating ? 'Generating...' : 'Download PDF'}
          </button>
        )}
      </div>

      {/* ==================== SUMMARY CARD ==================== */}
      <div className="sr-summary-card">
        <div className="sr-risk-score" data-severity={riskSeverity}>
          <div className="sr-risk-score-value">{results.riskScore || 0}</div>
          <div className="sr-risk-score-label">Risk</div>
        </div>
        <div className="sr-summary-body">
          <div className="sr-metric-grid">
            <div className="sr-metric-tile">
              <div className="sr-metric-value">{subfinder.totalSubdomains || 0}</div>
              <div className="sr-metric-label">Subdomains</div>
            </div>
            <div className="sr-metric-tile">
              <div className="sr-metric-value">{naabu.portCount || 0}</div>
              <div className="sr-metric-label">Open Ports</div>
            </div>
            <div className="sr-metric-tile">
              <div className="sr-metric-value">{sortedVulns.length || 0}</div>
              <div className="sr-metric-label">Vulnerabilities</div>
            </div>
            <div className="sr-metric-tile">
              <div className="sr-metric-value">{allTechnologies.length || 0}</div>
              <div className="sr-metric-label">Technologies</div>
            </div>
          </div>

          {/* Top 3 Actions */}
          {sortedVulns.length > 0 && (
            <div className="sr-top-actions">
              <div className="sr-top-actions-title">Top Actions</div>
              {sortedVulns.slice(0, 3).map((vuln, i) => (
                <div key={i} className="sr-action-item">
                  <span className="sr-vuln-severity-badge" data-severity={getSeverityKey(vuln.severity)}>
                    {vuln.severity}
                  </span>
                  <div className="sr-action-text">
                    <div className="sr-action-name">{vuln.name}</div>
                    {vuln.description && (
                      <div className="sr-action-desc">
                        {vuln.description.length > 120 ? vuln.description.substring(0, 120) + '...' : vuln.description}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Add as monitored asset CTA */}
          {isAuthenticated && isPaidTier && (
            <button
              className={`sr-add-asset-cta ${assetAdded ? 'added' : ''}`}
              onClick={handleAddAsset}
              disabled={assetAdded || addingAsset}
            >
              {assetAdded ? '✓ Asset added — monitoring started' : addingAsset ? 'Adding...' : '+ Add as monitored asset'}
            </button>
          )}
        </div>
      </div>

      {/* ==================== TARGET PROFILE ==================== */}
      <div className="sr-section">
        <div
          onClick={() => toggleSection('targetProfile')}
          className="sr-section-header"
        >
          <h2 className="sr-section-title">
            🎯 Target Profile
          </h2>
          <span className="sr-section-toggle">{expandedSections.targetProfile ? '▼' : '▶'}</span>
        </div>

        {expandedSections.targetProfile && (
          <div className="sr-section-body">
            {/* Primary Target Info */}
            <div className="sr-info-grid">
              <div className="sr-info-card">
                <div className="sr-info-label">Target</div>
                <div className="sr-info-value">{results.target}</div>
              </div>

              <div className="sr-info-card">
                <div className="sr-info-label">IP Address</div>
                <div className="sr-info-value sr-info-value--md">{dns.aRecords?.[0] || geo.ip || 'N/A'}</div>
              </div>

              <div className="sr-info-card">
                <div className="sr-info-label">Location</div>
                <div className="sr-info-value--text">
                  {geo.city && geo.country ? `${geo.city}, ${geo.region || ''} ${geo.country}` : 'N/A'}
                </div>
              </div>

              <div className="sr-info-card">
                <div className="sr-info-label">Risk Score</div>
                <div className="sr-risk-score-display">
                  <span className={`sr-risk-score-number sr-severity--${riskSeverity}`}>{results.riskScore || 0}</span>
                  <span className={`sr-risk-level-badge sr-severity-bg--${riskSeverity}`}>{headlineLevel}</span>
                </div>
              </div>
            </div>

            {/* Statistics Summary */}
            {(stats.totalVulnerabilities !== undefined || stats.openPorts !== undefined) && (
              <div className="sr-stats-block">
                <div className="sr-stats-title">
                  Scan Statistics
                </div>
                <div className="sr-stat-grid">
                  <div className="sr-stat-card--sm">
                    <div className="sr-stat-value">{stats.openPorts || naabu.portCount || 0}</div>
                    <div className="sr-stat-label">Open Ports</div>
                  </div>
                  <div className="sr-stat-card--sm">
                    <div className="sr-stat-value">{stats.totalVulnerabilities || nuclei.summary?.totalVulnerabilities || 0}</div>
                    <div className="sr-stat-label">Vulnerabilities</div>
                  </div>
                  <div className="sr-stat-card--sm">
                    <div className={`sr-stat-value ${stats.criticalVulnerabilities > 0 ? 'sr-severity--critical' : 'sr-severity--low'}`}>
                      {stats.criticalVulnerabilities || 0}
                    </div>
                    <div className="sr-stat-label">Critical</div>
                  </div>
                  <div className="sr-stat-card--sm">
                    <div className="sr-stat-value">{stats.subdomainsFound || subfinder.totalSubdomains || 0}</div>
                    <div className="sr-stat-label">Subdomains</div>
                  </div>
                  <div className="sr-stat-card--sm">
                    <div className="sr-stat-value">{stats.technologiesDetected || allTechnologies.length || 0}</div>
                    <div className="sr-stat-label">Technologies</div>
                  </div>
                </div>
              </div>
            )}

            {/* Network & Infrastructure Info */}
            <div className="sr-network-grid">
              <div className="sr-network-item">
                <span className="sr-network-label">ISP: </span>
                <span className="sr-network-value">{geo.isp || 'N/A'}</span>
              </div>
              <div className="sr-network-item">
                <span className="sr-network-label">Organization: </span>
                <span className="sr-network-value">{geo.organization || 'N/A'}</span>
              </div>
              <div className="sr-network-item">
                <span className="sr-network-label">ASN: </span>
                <span className="sr-network-value">{geo.asn || 'N/A'}</span>
              </div>
              <div className="sr-network-item">
                <span className="sr-network-label">Registrar: </span>
                <span className="sr-network-value">{registration.registrar || 'N/A'}</span>
              </div>
              <div className="sr-network-item">
                <span className="sr-network-label">Scan Duration: </span>
                <span className="sr-network-value--accent">{formatDuration(results.scanDuration)}</span>
              </div>
              <div className="sr-network-item">
                <span className="sr-network-label">Timezone: </span>
                <span className="sr-network-value">{geo.timezone || 'N/A'}</span>
              </div>
            </div>

            {/* DNS Records & Domain Registration */}
            {(dnsRecordRows.length > 0 || registration.registrar) && (
              <div className="sr-detail-grid">
                {registration.registrar && (
                  <div className="sr-subsection">
                    <h4 className="sr-subsection-title">Domain Registration</h4>
                    <div className="sr-network-grid">
                      <div className="sr-network-item"><span className="sr-network-label">Registrar: </span><span className="sr-network-value">{registration.registrar}</span></div>
                      {registration.registrationDate && <div className="sr-network-item"><span className="sr-network-label">Registered: </span><span className="sr-network-value">{formatDate(registration.registrationDate)}</span></div>}
                      {registration.expirationDate && <div className="sr-network-item"><span className="sr-network-label">Expires: </span><span className="sr-network-value">{formatDate(registration.expirationDate)}</span></div>}
                      {Array.isArray(registration.status) && registration.status.length > 0 && <div className="sr-network-item"><span className="sr-network-label">Status: </span><span className="sr-network-value">{registration.status.join(', ')}</span></div>}
                      {Array.isArray(registration.nameservers) && registration.nameservers.length > 0 && <div className="sr-network-item"><span className="sr-network-label">Nameservers: </span><span className="sr-network-value">{registration.nameservers.join(', ')}</span></div>}
                    </div>
                  </div>
                )}
                {dnsRecordRows.length > 0 && (
                  <div className="sr-subsection">
                    <h4 className="sr-subsection-title">DNS Records</h4>
                    <div className="sr-network-grid">
                      {dnsRecordRows.map(([type, values]) => (
                        <div key={type} className="sr-network-item">
                          <span className="sr-network-label">{type}: </span>
                          <span className="sr-network-value">{values.join(', ')}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ==================== ATTACK SURFACE ANALYSIS ==================== */}
      <div className="sr-section">
        <div
          onClick={() => toggleSection('attackSurface')}
          className="sr-section-header"
        >
          <h2 className="sr-section-title">
            🔍 Attack Surface Analysis
          </h2>
          <span className="sr-section-toggle">{expandedSections.attackSurface ? '▼' : '▶'}</span>
        </div>

        {expandedSections.attackSurface && (
          <div className="sr-section-body">
            {/* Summary Metrics */}
            <div className="sr-stat-grid sr-stat-grid--mb">
              <div className="sr-stat-card">
                <div className="sr-stat-value">{naabu.portCount || 0}</div>
                <div className="sr-stat-label">Open Ports</div>
              </div>
              <div className="sr-stat-card">
                <div className="sr-stat-value">{subfinder.totalSubdomains || 0}</div>
                <div className="sr-stat-label">Subdomains</div>
              </div>
              <div className="sr-stat-card">
                <div className="sr-stat-value">{katana.totalEndpoints || 0}</div>
                <div className="sr-stat-label">Endpoints</div>
              </div>
              <div className="sr-stat-card">
                <div className="sr-stat-value">{allTechnologies.length || 0}</div>
                <div className="sr-stat-label">Technologies</div>
              </div>
              <div className="sr-stat-card">
                <div className="sr-stat-value">{katana.parameters?.length || 0}</div>
                <div className="sr-stat-label">Parameters</div>
              </div>
            </div>

            {/* Technologies Detected */}
            {allTechnologies.length > 0 && (
              <div className="sr-tech-section">
                <div className="sr-tech-section-title">Technologies Detected</div>
                <div className="sr-tech-list">
                  {allTechnologies.map((tech, i) => (
                    <span key={i} className="sr-tech-tag">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Expandable Details Section */}
            <div
              onClick={() => toggleSection('attackSurfaceDetails')}
              className="sr-detail-toggle"
              style={{ marginBottom: expandedSections.attackSurfaceDetails ? '15px' : '0' }}
            >
              <span className="sr-detail-toggle-text">
                View Detailed Breakdown
              </span>
              <span className="sr-detail-toggle-icon">{expandedSections.attackSurfaceDetails ? '▼' : '▶'}</span>
            </div>

            {expandedSections.attackSurfaceDetails && (
              <div className="sr-detail-grid">
                {/* Ports & Services */}
                <div className="sr-subsection">
                  <h4 className="sr-subsection-title">Open Ports & Services</h4>

                  {/* High Risk Warning */}
                  {(naabu.riskClassification?.high?.length > 0 || naabu.remoteAccessExposed?.length > 0) && (
                    <div className="sr-risk-warning">
                      <div className="sr-risk-warning-text">⚠️ High-Risk Services Detected</div>
                    </div>
                  )}

                  <div className="sr-service-list">
                    {naabu.services?.map((svc, i) => {
                      const isHighRisk = naabu.riskClassification?.high?.some(h => h.port === svc.port);
                      return (
                        <div key={i} className={`sr-service-item ${isHighRisk ? 'sr-service-item--risk' : ''}`}>
                          <div>
                            <span className="sr-service-port">:{svc.port}</span>
                            <span className="sr-service-name">{svc.service}</span>
                          </div>
                          <span className="sr-service-protocol">{svc.protocol}</span>
                        </div>
                      );
                    })}
                  </div>

                  {/* Service Banners from zgrab2 */}
                  {zgrab2.services?.length > 0 && (
                    <div className="sr-banner-section">
                      <div className="sr-banner-label">Service Banners:</div>
                      {zgrab2.services.map((svc, i) => (
                        <div key={i} className="sr-banner-item">
                          :{svc.port} - {svc.product} {svc.version ? `v${svc.version}` : ''}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* HTTP Response Info */}
                {httpx.probes?.length > 0 && (
                  <div className="sr-subsection">
                    <h4 className="sr-subsection-title">HTTP Response Analysis</h4>
                    {httpx.probes.map((probe, i) => (
                      <div key={i} className="sr-probe-card">
                        <div className="sr-probe-header">
                          <span className="sr-probe-url">{probe.url}</span>
                          <span className={`sr-status-badge ${probe.statusCode >= 200 && probe.statusCode < 300 ? 'sr-status-badge--ok' : 'sr-status-badge--warn'}`}>
                            {probe.statusCode}
                          </span>
                        </div>
                        {probe.title && (
                          <div className="sr-probe-detail">
                            <span className="sr-probe-detail-label">Title: </span>{probe.title}
                          </div>
                        )}
                        {probe.server && (
                          <div className="sr-probe-detail--muted">
                            <span>Server: </span><span className="sr-probe-detail--accent">{probe.server}</span>
                          </div>
                        )}
                        {probe.contentLength && (
                          <div className="sr-probe-detail--muted">
                            Content Length: {probe.contentLength} bytes
                          </div>
                        )}
                      </div>
                    ))}

                    {/* HTTP Summary */}
                    {httpx.summary && (
                      <div className="sr-probe-summary">
                        <div className="sr-stat-card--sm">
                          <div className="sr-stat-value--md">{httpx.summary.liveHosts || 0}</div>
                          <div className="sr-stat-label">Live Hosts</div>
                        </div>
                        <div className="sr-stat-card--sm">
                          <div className={`sr-stat-value--md ${httpx.summary.httpsEnabled ? 'sr-severity--low' : 'sr-severity--medium'}`}>
                            {httpx.summary.httpsEnabled ? 'Yes' : 'No'}
                          </div>
                          <div className="sr-stat-label">HTTPS</div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Endpoint Discovery (Katana) */}
                {katana.summary && (
                  <div className="sr-subsection">
                    <h4 className="sr-subsection-title">Endpoint Discovery</h4>
                    <div className="sr-mini-grid sr-mini-grid--mb">
                      <div className="sr-stat-card--sm">
                        <div className="sr-stat-value--sm">{katana.summary.htmlEndpoints || 0}</div>
                        <div className="sr-stat-label">HTML</div>
                      </div>
                      <div className="sr-stat-card--sm">
                        <div className="sr-stat-value--sm">{katana.summary.jsEndpoints || 0}</div>
                        <div className="sr-stat-label">JavaScript</div>
                      </div>
                      <div className="sr-stat-card--sm">
                        <div className="sr-stat-value--sm">{katana.summary.apiEndpoints || 0}</div>
                        <div className="sr-stat-label">API</div>
                      </div>
                      <div className="sr-stat-card--sm">
                        <div className="sr-stat-value--sm">{katana.summary.uniquePaths || 0}</div>
                        <div className="sr-stat-label">Unique Paths</div>
                      </div>
                    </div>

                    {katana.parameters?.length > 0 && (
                      <div>
                        <div className="sr-param-label">Parameters Found:</div>
                        <div className="sr-param-list">
                          {katana.parameters.map((param, i) => (
                            <span key={i} className="sr-param-tag">
                              {param}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Subdomain Categories */}
                {subfinder.summary && (
                  <div className="sr-subsection">
                    <h4 className="sr-subsection-title">Subdomain Analysis</h4>
                    <div className="sr-mini-grid sr-mini-grid--3col">
                      <div className="sr-stat-card--sm">
                        <div className="sr-stat-value--sm">{subfinder.summary.wwwCount || 0}</div>
                        <div className="sr-stat-label">WWW</div>
                      </div>
                      <div className="sr-stat-card--sm">
                        <div className="sr-stat-value--sm">{subfinder.summary.mailCount || 0}</div>
                        <div className="sr-stat-label">Mail</div>
                      </div>
                      <div className="sr-stat-card--sm">
                        <div className="sr-stat-value--sm">{subfinder.summary.apiCount || 0}</div>
                        <div className="sr-stat-label">API</div>
                      </div>
                      <div className="sr-stat-card--sm">
                        <div className="sr-stat-value--sm sr-severity--medium">{subfinder.summary.adminCount || 0}</div>
                        <div className="sr-stat-label">Admin</div>
                      </div>
                      <div className="sr-stat-card--sm">
                        <div className="sr-stat-value--sm sr-severity--medium">{subfinder.summary.devCount || 0}</div>
                        <div className="sr-stat-label">Dev</div>
                      </div>
                      <div className="sr-stat-card--sm">
                        <div className="sr-stat-value--sm">{subfinder.summary.interestingFindings || 0}</div>
                        <div className="sr-stat-label">Interesting</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ==================== DISCOVERED URLS & SUBDOMAINS ==================== */}
      {(katana.totalEndpoints > 0 || katana.interestingFiles?.length > 0 || subfinder.subdomains?.length > 0 || subfinder.totalSubdomains > 0) && (
        <div className="sr-section">
          <div onClick={() => toggleSection('discoveredUrls')} className="sr-section-header">
            <h2 className="sr-section-title">🗺️ Discovered URLs &amp; Subdomains</h2>
            <span className="sr-section-toggle">{expandedSections.discoveredUrls ? '▼' : '▶'}</span>
          </div>

          {expandedSections.discoveredUrls && (
            <div className="sr-section-body">
              {/* Limited-view notice — fast scan is a capped snapshot */}
              <div className="sr-limited-note">
                <span className="sr-limited-badge">Limited view</span>
                Fast scan shows a capped snapshot of the attack surface.{' '}
                {isAuthenticated && isPaidTier
                  ? 'Add this as a monitored asset for full, continuous subdomain & URL discovery.'
                  : 'Monitored assets get full, continuous subdomain & URL discovery.'}
              </div>

              {/* Interesting endpoints (highlighted) */}
              {katana.interestingFiles?.length > 0 && (
                <div className="sr-subsection">
                  <h4 className="sr-subsection-title sr-subsection-title--accent">
                    Interesting Endpoints <span className="sr-count-pill">{katana.interestingFiles.length}</span>
                  </h4>
                  <div className="sr-url-list">
                    {katana.interestingFiles.map((f, i) => (
                      <div key={i} className="sr-url-item sr-url-item--interesting">
                        <span className="sr-url-text">{f.url}</span>
                        {f.pattern && <span className="sr-url-tag">{f.pattern}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Categorized discovered URLs (katana) */}
              {katana.endpoints && typeof katana.endpoints === 'object' &&
                Object.entries(katana.endpoints)
                  .filter(([, urls]) => Array.isArray(urls) && urls.length > 0)
                  .map(([cat, urls]) => {
                    const key = `urls_${cat}`;
                    const shown = expandedSections[key] ? urls.length : 10;
                    return (
                      <div key={cat} className="sr-subsection">
                        <h4 className="sr-subsection-title">
                          {cat.toUpperCase()} <span className="sr-count-pill">{urls.length}</span>
                        </h4>
                        <div className="sr-url-list">
                          {urls.slice(0, shown).map((u, i) => (
                            <div key={i} className="sr-url-item"><span className="sr-url-text">{u}</span></div>
                          ))}
                        </div>
                        {urls.length > 10 && (
                          <button className="sr-show-more-btn" onClick={() => toggleSection(key)}>
                            {expandedSections[key] ? 'Show less' : `Show all ${urls.length}`}
                          </button>
                        )}
                      </div>
                    );
                  })}

              {/* Subdomains + limited CTA */}
              {(subfinder.subdomains?.length > 0 || subfinder.totalSubdomains > 0) && (
                <div className="sr-subsection">
                  <h4 className="sr-subsection-title">
                    Subdomains <span className="sr-count-pill">{subfinder.totalSubdomains || subfinder.subdomains?.length || 0}</span>
                  </h4>
                  {subfinder.subdomains?.length > 0 ? (
                    <>
                      <div className="sr-url-list">
                        {subfinder.subdomains
                          .slice(0, expandedSections.allSubs ? subfinder.subdomains.length : 15)
                          .map((s, i) => (
                            <div key={i} className="sr-url-item">
                              <span className="sr-url-text">{typeof s === 'string' ? s : (s.subdomain || s.host || s.name || '')}</span>
                            </div>
                          ))}
                      </div>
                      {subfinder.subdomains.length > 15 && (
                        <button className="sr-show-more-btn" onClick={() => toggleSection('allSubs')}>
                          {expandedSections.allSubs ? 'Show less' : `Show all ${subfinder.subdomains.length}`}
                        </button>
                      )}
                    </>
                  ) : (
                    <div className="sr-muted-note">
                      {subfinder.totalSubdomains} found — add as a monitored asset to enumerate them all.
                    </div>
                  )}
                  {subfinder.truncated && (
                    <div className="sr-muted-note">List truncated for fast scan. {subfinder.note || ''}</div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ==================== DriftAlarm BRIEF ==================== */}
      <div className="sr-section">
        <div
          onClick={() => toggleSection('r3dteamBrief')}
          className="sr-section-header"
        >
          <h2 className="sr-section-title">
            🔴 DriftAlarm Brief
          </h2>
          <span className="sr-section-toggle">{expandedSections.r3dteamBrief ? '▼' : '▶'}</span>
        </div>

        {expandedSections.r3dteamBrief && (
          <div className="sr-section-body">
            {/* Threat Level & Confidence */}
            <div className="sr-threat-display">
              <div className="sr-threat-card" data-severity={severityKeyOf(ai.overallThreatLevel || results.riskLevel)}>
                <span className="sr-threat-label">Threat Level:</span>
                <span className={`sr-threat-value sr-severity--${severityKeyOf(ai.overallThreatLevel || results.riskLevel)}`}>
                  {ai.overallThreatLevel || results.riskLevel || 'N/A'}
                </span>
              </div>

              {ai.confidenceScore && (
                <div className="sr-confidence-card">
                  <span className="sr-confidence-label">AI Confidence:</span>
                  <span className="sr-confidence-value">{ai.confidenceScore}%</span>
                </div>
              )}
            </div>

            {/* Executive Summary */}
            {ai.executiveSummary && (
              <div className="sr-exec-summary-wrapper">
                <h4 className="sr-brief-subtitle">Executive Summary</h4>
                <p className="sr-exec-summary">
                  {ai.executiveSummary}
                </p>
              </div>
            )}

            {/* Key Findings */}
            {ai.keyFindings?.length > 0 && (
              <div>
                <h4 className="sr-brief-subtitle">Key Findings</h4>
                {ai.keyFindings.map((finding, i) => (
                  <div key={i} className="sr-finding-item">
                    {finding}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ==================== VULNERABILITIES ==================== */}
      <div className="sr-section">
        <div
          onClick={() => toggleSection('vulnerabilities')}
          className="sr-section-header"
        >
          <h2 className="sr-section-title">
            ⚡ Vulnerabilities ({nuclei.summary?.totalVulnerabilities || sortedVulns.length || 0})
          </h2>
          <span className="sr-section-toggle">{expandedSections.vulnerabilities ? '▼' : '▶'}</span>
        </div>

        {expandedSections.vulnerabilities && (
          <div className="sr-section-body">
            {/* Severity Summary */}
            <div className="sr-severity-summary">
              {[
                { label: 'Critical', key: 'critical', count: nuclei.summary?.criticalCount || 0 },
                { label: 'High', key: 'high', count: nuclei.summary?.highCount || 0 },
                { label: 'Medium', key: 'medium', count: nuclei.summary?.mediumCount || 0 },
                { label: 'Low', key: 'low', count: nuclei.summary?.lowCount || 0 },
                { label: 'Info', key: 'info', count: nuclei.summary?.infoCount || 0 }
              ].map((sev, i) => (
                <div key={i} className={`sr-severity-box ${sev.count > 0 ? 'sr-severity-box--active' : ''}`} data-severity={sev.count > 0 ? sev.key : undefined}>
                  <div className="sr-severity-box-value">{sev.count}</div>
                  <div className="sr-severity-box-label">{sev.label}</div>
                </div>
              ))}
            </div>

            {/* Vulnerability Details */}
            {sortedVulns.length > 0 ? (
              <div>
                <div className="sr-vuln-list-header">
                  <h4 className="sr-vuln-list-title">
                    Findings by Severity {showAllVulns ? '(showing 15)' : '(showing 5)'}
                  </h4>
                  {sortedVulns.length > 5 && (
                    <button
                      onClick={() => setShowAllVulns(!showAllVulns)}
                      className="sr-show-more-btn"
                    >
                      {showAllVulns ? 'Show Less' : `Show More (${Math.min(15, sortedVulns.length)})`}
                    </button>
                  )}
                </div>

                {displayVulns.map((vuln, i) => (
                  <div
                    key={i}
                    className="sr-vuln-card"
                    data-severity={getSeverityKey(vuln.severity)}
                    onClick={() => setExpandedVulns(prev => ({ ...prev, [i]: !prev[i] }))}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="sr-vuln-header">
                      <div>
                        <h5 className="sr-vuln-name">{vuln.name}</h5>
                        <span className="sr-vuln-template">{vuln.templateId}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span className="sr-vuln-severity-badge" data-severity={getSeverityKey(vuln.severity)}>
                          {vuln.severity}
                        </span>
                        <span aria-hidden="true" style={{ opacity: 0.7, fontSize: '0.9rem' }}>
                          {expandedVulns[i] ? '▼' : '▶'}
                        </span>
                      </div>
                    </div>

                    {vuln.description && (
                      <p className="sr-vuln-description">
                        {vuln.description.length > 200 ? vuln.description.substring(0, 200) + '...' : vuln.description}
                      </p>
                    )}

                    {vuln.matched && (
                      <div className="sr-vuln-matched">
                        <span className="sr-vuln-matched-label">Matched: </span>{vuln.matched}
                      </div>
                    )}

                    {vuln.tags?.length > 0 && (
                      <div className="sr-vuln-tags">
                        {vuln.tags.slice(0, 6).map((tag, j) => (
                          <span key={j} className="sr-vuln-tag">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}

                    {expandedVulns[i] && (
                      <div onClick={(e) => e.stopPropagation()} style={{ marginTop: '14px', cursor: 'default' }}>
                        <HttpTransactionView
                          httpTransaction={parseHttpTransactionFromRaw(vuln.raw, vuln.matched)}
                          matcherPayload={vuln.matcherName || vuln.extractedResults?.[0]}
                          showEmpty
                        />
                      </div>
                    )}
                  </div>
                ))}

                {/* Upgrade teaser for free tier after first 5 findings */}
                {!isPaidTier && sortedVulns.length > 5 && (
                  <div className="sr-upgrade-teaser">
                    <div className="sr-upgrade-teaser-count">
                      {sortedVulns.length - 5} additional vulnerabilities found
                    </div>
                    <button
                      className="sr-upgrade-teaser-cta"
                      onClick={() => navigate('/upgrade')}
                    >
                      Upgrade to Standard to see all details
                    </button>
                  </div>
                )}

                {/* Blurred remaining findings for free tier */}
                {!isPaidTier && sortedVulns.length > 5 && (
                  <div style={{ position: 'relative' }}>
                    {sortedVulns.slice(5, 8).map((vuln, i) => (
                      <div key={`blur-${i}`} className="sr-vuln-card sr-finding-blurred" data-severity={getSeverityKey(vuln.severity)}>
                        <div className="sr-vuln-header">
                          <div>
                            <h5 className="sr-vuln-name">{vuln.name}</h5>
                          </div>
                          <span className="sr-vuln-severity-badge" data-severity={getSeverityKey(vuln.severity)}>
                            {vuln.severity}
                          </span>
                        </div>
                        {vuln.description && (
                          <p className="sr-vuln-description">{vuln.description.substring(0, 100)}</p>
                        )}
                      </div>
                    ))}
                    <div className="sr-upgrade-overlay">
                      <div className="sr-upgrade-overlay-text">Unlock full vulnerability details</div>
                      <button
                        className="sr-upgrade-teaser-cta"
                        onClick={() => navigate('/upgrade')}
                      >
                        See Pricing
                      </button>
                    </div>
                  </div>
                )}

                {sortedVulns.length > 15 && showAllVulns && (
                  <div className="sr-vuln-overflow">
                    Showing 15 of {sortedVulns.length} vulnerabilities. See Full JSON for complete list.
                  </div>
                )}
              </div>
            ) : (
              <div className="sr-no-vulns">
                <div className="sr-no-vulns-icon">✓</div>
                <div className="sr-no-vulns-title">No Critical Vulnerabilities Detected</div>
                <div className="sr-no-vulns-text">Target shows good baseline security posture</div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ==================== VALIDATE RESULTS ==================== */}
      {validateJobId && (
        <div className="sr-section">
          <div
            onClick={() => toggleSection('validateResults')}
            className="sr-section-header sr-section-header--validate"
          >
            <div className="sr-validate-title-row">
              <span className="sr-validate-icon">V</span>
              <h2 className="sr-section-title">
                VALIDATE Results
              </h2>
              {validateStatus && (
                <span className={`sr-validate-status ${
                  validateStatus === 'complete' ? 'sr-validate-status--complete' :
                  validateStatus === 'running' ? 'sr-validate-status--running' :
                  'sr-validate-status--default'
                }`}>
                  {validateStatus}
                </span>
              )}
            </div>
            <span className="sr-section-toggle">
              {expandedSections.validateResults ? '▼' : '▶'}
            </span>
          </div>

          {expandedSections.validateResults && (
            <div className="sr-section-body--flush">
              {validateStatus === 'complete' ? (
                <ValidateResults jobId={validateJobId} scanId={scanId} />
              ) : validateStatus === 'running' ? (
                <div className="sr-validate-loading">
                  <div className="sr-validate-spinner"></div>
                  <div className="sr-validate-loading-title">
                    Validation in Progress
                  </div>
                  <div className="sr-validate-loading-text">
                    Testing vulnerabilities for exploitability...
                  </div>
                </div>
              ) : validateStatus === 'pending' || validateStatus === 'waiting' ? (
                <div className="sr-validate-pending">
                  <div className="sr-validate-pending-icon">V</div>
                  <div className="sr-validate-loading-title">
                    Validation Queued
                  </div>
                  <div className="sr-validate-loading-text">
                    Waiting for scan vulnerabilities to be processed...
                  </div>
                </div>
              ) : (
                <div className="sr-validate-pending">
                  <div className="sr-validate-status-text">
                    VALIDATE status: {validateStatus || 'Unknown'}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ==================== DEFENSIVE POSTURE & RECOMMENDATIONS ==================== */}
      <div className="sr-section sr-section--green">
        <div
          onClick={() => toggleSection('defensivePosture')}
          className="sr-section-header sr-section-header--green"
        >
          <h2 className="sr-section-title sr-section-title--green">
            🛡️ Defensive Posture & Recommendations
          </h2>
          <span className="sr-section-toggle sr-section-toggle--green">{expandedSections.defensivePosture ? '▼' : '▶'}</span>
        </div>

        {expandedSections.defensivePosture && (
          <div className="sr-section-body">
            {/* Defense Status Cards */}
            <div className="sr-defense-grid">
              {/* WAF Status */}
              <div className={`sr-defense-card ${wafw00f.wafDetected ? 'sr-defense-card--active' : 'sr-defense-card--inactive'}`}>
                <div className="sr-defense-icon">{wafw00f.wafDetected ? '🛡️' : '⚠️'}</div>
                <div className={`sr-defense-label ${wafw00f.wafDetected ? 'sr-defense-label--active' : 'sr-defense-label--inactive'}`}>
                  WAF {wafw00f.wafDetected ? 'ACTIVE' : 'NOT DETECTED'}
                </div>
                {wafw00f.wafName && <div className="sr-defense-detail">{wafw00f.wafName}</div>}
              </div>

              {/* HTTPS Status */}
              <div className={`sr-defense-card ${httpx.summary?.httpsEnabled ? 'sr-defense-card--active' : 'sr-defense-card--warn'}`}>
                <div className="sr-defense-icon">{httpx.summary?.httpsEnabled ? '🔒' : '🔓'}</div>
                <div className={`sr-defense-label ${httpx.summary?.httpsEnabled ? 'sr-defense-label--active' : 'sr-defense-label--warn'}`}>
                  HTTPS {httpx.summary?.httpsEnabled ? 'ENABLED' : 'CHECK NEEDED'}
                </div>
              </div>

              {/* Exposed Services Risk */}
              <div className={`sr-defense-card ${(naabu.remoteAccessExposed?.length > 0 || naabu.databasesExposed?.length > 0) ? 'sr-defense-card--inactive' : 'sr-defense-card--active'}`}>
                <div className="sr-defense-icon">
                  {(naabu.remoteAccessExposed?.length > 0 || naabu.databasesExposed?.length > 0) ? '🚨' : '✓'}
                </div>
                <div className={`sr-defense-label ${(naabu.remoteAccessExposed?.length > 0 || naabu.databasesExposed?.length > 0) ? 'sr-defense-label--inactive' : 'sr-defense-label--active'}`}>
                  {(naabu.remoteAccessExposed?.length > 0 || naabu.databasesExposed?.length > 0) ? 'EXPOSURE RISK' : 'SERVICES OK'}
                </div>
                {naabu.remoteAccessExposed?.length > 0 && (
                  <div className="sr-defense-sub">
                    {naabu.remoteAccessExposed.map(s => `${s.service}:${s.port}`).join(', ')}
                  </div>
                )}
              </div>
            </div>

            {/* Risk Factors */}
            {results.riskFactors?.length > 0 && (
              <div className="sr-risk-factors">
                <div className="sr-risk-factors-title">Risk Factors Identified:</div>
                <div className="sr-risk-tag-list">
                  {results.riskFactors.map((factor, i) => (
                    <span key={i} className="sr-risk-tag">
                      {factor}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Prioritized Recommendations */}
            {ai.prioritizedRecommendations?.length > 0 && (
              <div>
                <div
                  onClick={(e) => { e.stopPropagation(); toggleSection('recommendations'); }}
                  className="sr-rec-header"
                >
                  <h4 className="sr-rec-title">
                    Prioritized Recommendations ({ai.prioritizedRecommendations.length})
                  </h4>
                  <span className="sr-rec-toggle">{expandedSections.recommendations ? '▼' : '▶'}</span>
                </div>

                {expandedSections.recommendations && ai.prioritizedRecommendations.map((rec, i) => (
                  <div key={i} className="sr-rec-card" data-priority={getSeverityKey(rec.priority)}>
                    <div className="sr-rec-meta-row">
                      <span className="sr-rec-rank" data-priority={getSeverityKey(rec.priority)}>{rec.rank}</span>
                      <span className="sr-rec-priority-badge" data-priority={getSeverityKey(rec.priority)}>{rec.priority}</span>
                      <span className="sr-rec-detail">
                        {rec.effort} effort • {rec.impact} impact • {rec.timeline}
                      </span>
                    </div>
                    <p className="sr-rec-text">
                      {rec.recommendation}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ==================== TOOL SUMMARIES ==================== */}
      <div className="sr-section sr-section--neutral">
        <div
          onClick={() => toggleSection('toolSummaries')}
          className="sr-section-header sr-section-header--neutral"
        >
          <h2 className="sr-section-title sr-section-title--muted">
            📊 Tool Summary Cards
          </h2>
          <span className="sr-section-toggle sr-section-toggle--muted">{expandedSections.toolSummaries ? '▼' : '▶'}</span>
        </div>

        {expandedSections.toolSummaries && (
          <div className="sr-section-body">
            <div className="sr-tool-grid">

              {/* Naabu Summary */}
              {naabu.summary && (
                <div className="sr-tool-card">
                  <h4 className="sr-tool-title">🔌 Port Discovery</h4>
                  <div className="sr-mini-grid">
                    <div className="sr-stat-card--sm">
                      <div className="sr-stat-value--md">{naabu.summary.totalOpen}</div>
                      <div className="sr-stat-label">Total Open</div>
                    </div>
                    <div className="sr-stat-card--sm">
                      <div className={`sr-stat-value--md ${naabu.summary.highRiskCount > 0 ? 'sr-severity--critical' : 'sr-severity--low'}`}>{naabu.summary.highRiskCount}</div>
                      <div className="sr-stat-label">High Risk</div>
                    </div>
                    <div className="sr-stat-card--sm">
                      <div className={`sr-stat-value--md ${naabu.summary.remoteAccessCount > 0 ? 'sr-severity--medium' : 'sr-severity--low'}`}>{naabu.summary.remoteAccessCount}</div>
                      <div className="sr-stat-label">Remote Access</div>
                    </div>
                    <div className="sr-stat-card--sm">
                      <div className={`sr-stat-value--md ${naabu.summary.databaseCount > 0 ? 'sr-severity--critical' : 'sr-severity--low'}`}>{naabu.summary.databaseCount}</div>
                      <div className="sr-stat-label">Databases</div>
                    </div>
                  </div>
                </div>
              )}

              {/* zgrab2 Summary */}
              {zgrab2.summary && (
                <div className="sr-tool-card">
                  <h4 className="sr-tool-title">📡 Service Grabber</h4>
                  <div className="sr-mini-grid">
                    <div className="sr-stat-card--sm">
                      <div className="sr-stat-value--md">{zgrab2.summary.servicesIdentified}</div>
                      <div className="sr-stat-label">Services ID'd</div>
                    </div>
                    <div className="sr-stat-card--sm">
                      <div className="sr-stat-value--md">{zgrab2.summary.bannersCollected}</div>
                      <div className="sr-stat-label">Banners</div>
                    </div>
                  </div>
                  {zgrab2.sshInfo && (
                    <div className="sr-tool-info">
                      <div className="sr-tool-info-label">SSH Info:</div>
                      <div className="sr-tool-info-value">{zgrab2.sshInfo.software}</div>
                    </div>
                  )}
                </div>
              )}

              {/* httpx Summary */}
              {httpx.summary && (
                <div className="sr-tool-card">
                  <h4 className="sr-tool-title">🌐 HTTP Probe</h4>
                  <div className="sr-mini-grid">
                    <div className="sr-stat-card--sm">
                      <div className="sr-stat-value--md">{httpx.summary.totalProbes}</div>
                      <div className="sr-stat-label">Probes</div>
                    </div>
                    <div className="sr-stat-card--sm">
                      <div className="sr-stat-value--md sr-severity--low">{httpx.summary.liveHosts}</div>
                      <div className="sr-stat-label">Live Hosts</div>
                    </div>
                    <div className="sr-stat-card--sm">
                      <div className="sr-stat-value--md">{httpx.summary.uniqueTechnologies}</div>
                      <div className="sr-stat-label">Technologies</div>
                    </div>
                    <div className="sr-stat-card--sm">
                      <div className={`sr-stat-value--md ${httpx.summary.httpsEnabled ? 'sr-severity--low' : 'sr-severity--medium'}`}>
                        {httpx.summary.httpsEnabled ? '✓' : '✗'}
                      </div>
                      <div className="sr-stat-label">HTTPS</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Katana Summary */}
              {katana.summary && (
                <div className="sr-tool-card">
                  <h4 className="sr-tool-title">🕷️ Web Crawler</h4>
                  <div className="sr-mini-grid sr-mini-grid--3col">
                    <div className="sr-stat-card--sm">
                      <div className="sr-stat-value--md">{katana.summary.totalUrls}</div>
                      <div className="sr-stat-label">Total URLs</div>
                    </div>
                    <div className="sr-stat-card--sm">
                      <div className="sr-stat-value--md">{katana.summary.uniquePaths}</div>
                      <div className="sr-stat-label">Unique Paths</div>
                    </div>
                    <div className="sr-stat-card--sm">
                      <div className="sr-stat-value--md">{katana.summary.parametersFound}</div>
                      <div className="sr-stat-label">Params</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Subfinder Summary */}
              {subfinder.summary && (
                <div className="sr-tool-card">
                  <h4 className="sr-tool-title">🔍 Subdomain Finder</h4>
                  <div className="sr-mini-grid sr-mini-grid--3col">
                    <div className="sr-stat-card--sm">
                      <div className="sr-stat-value--md">{subfinder.summary.total}</div>
                      <div className="sr-stat-label">Total</div>
                    </div>
                    <div className="sr-stat-card--sm">
                      <div className="sr-stat-value--md sr-severity--medium">{subfinder.summary.adminCount}</div>
                      <div className="sr-stat-label">Admin</div>
                    </div>
                    <div className="sr-stat-card--sm">
                      <div className="sr-stat-value--md">{subfinder.summary.interestingFindings}</div>
                      <div className="sr-stat-label">Interesting</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Nuclei Summary */}
              {nuclei.summary && (
                <div className="sr-tool-card sr-tool-card--brand">
                  <h4 className="sr-tool-title sr-tool-title--brand">💥 Vulnerability Scanner</h4>
                  <div className="sr-mini-grid sr-mini-grid--3col">
                    <div className="sr-stat-card--sm">
                      <div className="sr-stat-value--md sr-stat-value--brand">{nuclei.summary.totalVulnerabilities}</div>
                      <div className="sr-stat-label">Total</div>
                    </div>
                    <div className="sr-stat-card--sm">
                      <div className={`sr-stat-value--md ${nuclei.summary.criticalCount > 0 ? 'sr-severity--critical' : 'sr-severity--low'}`}>{nuclei.summary.criticalCount}</div>
                      <div className="sr-stat-label">Critical</div>
                    </div>
                    <div className="sr-stat-card--sm">
                      <div className={`sr-stat-value--md ${nuclei.summary.highCount > 0 ? 'sr-severity--high' : 'sr-severity--low'}`}>{nuclei.summary.highCount}</div>
                      <div className="sr-stat-label">High</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ==================== RAW TOOL DATA ==================== */}
      <div className="sr-section sr-section--neutral">
        <div
          onClick={() => toggleSection('rawToolData')}
          className="sr-section-header sr-section-header--neutral"
        >
          <h2 className="sr-section-title sr-section-title--dim">
            🛠️ Raw Tool Data (JSON)
          </h2>
          <span className="sr-section-toggle sr-section-toggle--muted">{expandedSections.rawToolData ? '▼' : '▶'}</span>
        </div>

        {expandedSections.rawToolData && (
          <div className="sr-section-body">
            <div className="sr-raw-grid">
              {[
                { name: 'dns', data: dns },
                { name: 'naabu', data: naabu },
                { name: 'zgrab2', data: zgrab2 },
                { name: 'httpx', data: httpx },
                { name: 'katana', data: katana },
                { name: 'subfinder', data: subfinder },
                { name: 'nuclei', data: nuclei },
                { name: 'wafw00f', data: wafw00f },
                { name: 'geolocation', data: geo },
                { name: 'registration', data: registration },
                { name: 'aiAnalysis', data: ai }
              ].map((tool, i) => (
                <div key={i} className="sr-raw-card">
                  <div className="sr-raw-header">
                    <span className="sr-raw-name">{tool.name}</span>
                    <button
                      onClick={() => copyToClipboard(JSON.stringify(tool.data, null, 2))}
                      className="sr-raw-copy-btn"
                    >
                      Copy
                    </button>
                  </div>
                  <pre className="sr-raw-content">
                    {JSON.stringify(tool.data, null, 2)}
                  </pre>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ==================== FULL JSON ==================== */}
      <div className="sr-section sr-section--neutral">
        <div
          onClick={() => toggleSection('fullJson')}
          className="sr-section-header sr-section-header--neutral"
        >
          <h2 className="sr-section-title sr-section-title--dim">
            📄 Full JSON Response
          </h2>
          <span className="sr-section-toggle sr-section-toggle--muted">{expandedSections.fullJson ? '▼' : '▶'}</span>
        </div>

        {expandedSections.fullJson && (
          <div className="sr-section-body">
            <button
              onClick={() => copyToClipboard(JSON.stringify(results, null, 2))}
              className="sr-json-copy-btn"
            >
              📋 Copy Full JSON
            </button>
            <pre className="sr-json-pre">
              {JSON.stringify(results, null, 2)}
            </pre>
          </div>
        )}
      </div>

      {/* ==================== SCAN METADATA ==================== */}
      <div className="sr-metadata">
        <div className="sr-metadata-grid">
          <div>
            <span className="sr-metadata-label">Scan ID: </span>
            <span className="sr-metadata-value--accent">{results.scanId}</span>
          </div>
          <div>
            <span className="sr-metadata-label">Version: </span>
            <span className="sr-metadata-value">{metadata.scannerVersion || results.scanVersion}</span>
          </div>
          <div>
            <span className="sr-metadata-label">Timestamp: </span>
            <span className="sr-metadata-value">{results.timestamp ? new Date(results.timestamp).toLocaleString() : 'N/A'}</span>
          </div>
          <div>
            <span className="sr-metadata-label">AI Model: </span>
            <span className="sr-metadata-value">{metadata.aiModel || 'Claude'}</span>
          </div>
        </div>
      </div>

      {/* ==================== UPGRADE CARD (free authenticated users) ==================== */}
      {isAuthenticated && tier === 'free' && (
        <div className="sr-upgrade-card">
          <div className="sr-upgrade-teaser-count">
            {fastScanUsed}/{fastScanLimit} free scans used this week
          </div>
          <table className="sr-upgrade-card-table">
            <thead>
              <tr>
                <th>Feature</th>
                <th>Free</th>
                <th className="highlight">Standard</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Scans/week</td>
                <td>5</td>
                <td className="highlight">25</td>
              </tr>
              <tr>
                <td>Vuln scanning</td>
                <td>--</td>
                <td className="highlight">10/week</td>
              </tr>
              <tr>
                <td>Drift monitoring</td>
                <td>--</td>
                <td className="highlight">Included</td>
              </tr>
            </tbody>
          </table>
          <button
            className="sr-upgrade-teaser-cta"
            onClick={() => navigate('/upgrade')}
          >
            Upgrade to Standard -- $29/mo
          </button>
        </div>
      )}

      {/* ==================== FOOTER ==================== */}
      <div className="sr-footer">
        <button onClick={() => navigate('/reports')} className="sr-btn-secondary">
          View All Reports
        </button>
        <button onClick={() => navigate('/fast-scan')} className="sr-btn-primary">
          New Fast Scan
        </button>
      </div>

      {/* ==================== CONVERSION MODAL (unauthenticated) ==================== */}
      {showConversionModal && (
        <div className="sr-conversion-modal-overlay" onClick={dismissConversionModal}>
          <div className="sr-conversion-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sr-conversion-modal-heading">Save your scan results</div>
            <div className="sr-conversion-modal-sub">
              Create a free account in 30 seconds to keep your results and get 5 free scans per week.
            </div>
            <button
              className="sr-conversion-modal-cta"
              onClick={() => {
                sessionStorage.setItem('postLoginRedirect', `/scan-result/${scanId}`);
                navigate('/');
              }}
            >
              Create Free Account
            </button>
            <button
              className="sr-conversion-modal-dismiss"
              onClick={dismissConversionModal}
            >
              Maybe later
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScanResults;
